/**
 * FoodBusinessesDirectory — public directory of every US food
 * business (restaurants, cafes, supermarkets, bakeries, food trucks,
 * farms, food banks, distributors, breweries, wineries…).
 *
 * Route: /food-pulse/businesses
 * Backend: GET /api/food-businesses, /api/food-businesses/stats
 *
 * Filters: text search, business_type, state, metro area, verified
 * only. Cards link into the per-business profile.
 */
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Search, MapPin, Store, Utensils, Coffee, ShoppingCart, Wheat,
  Truck, HeartHandshake, BadgeCheck, ArrowRight, Sparkles, Smartphone,
  Tractor, ChefHat, ChevronDown,
} from "lucide-react";
import FoodPulseSlogan from "@/components/FoodPulseSlogan";
import SeoHead from "@/components/SeoHead";
import { FP_TAGLINES } from "@/config/foodPulseTaglines";
import { categoryIllustration, categoryHeroIllustration } from "@/config/categoryIllustrations";

const API = process.env.REACT_APP_BACKEND_URL;

// Curated user-facing groupings. Each chip maps to one or many
// canonical business_type values on the backend.
const TYPE_CHIPS = [
  { key: "",                                    label: "All",             icon: Store,          hero: { kicker: "every food business", h1: "Every US food business, one open directory.", tag: FP_TAGLINES.businesses } },
  { key: "restaurant,fast_food,food_court",     label: "Restaurants",     icon: Utensils,       hero: { kicker: "restaurants",         h1: "Taste the best around you.",         tag: FP_TAGLINES.restaurants } },
  { key: "cafe,coffee_shop",                    label: "Cafes & coffee",  icon: Coffee,         hero: { kicker: "cafes & coffee",      h1: "Every neighbourhood has its brew.",  tag: "Discover cafes and coffee shops in your city." } },
  { key: "bakery,pastry",                       label: "Bakeries",        icon: Wheat,          hero: { kicker: "bakeries",            h1: "Warm bread, still-warm memories.",   tag: "Bakeries and pastry shops in every neighbourhood." } },
  { key: "supermarket,grocery,convenience_store,health_food", label: "Groceries / Supermarkets", icon: ShoppingCart, hero: { kicker: "groceries & supermarkets", h1: "Everything fresh, close to home.", tag: FP_TAGLINES.groceries } },
  { key: "food_truck",                          label: "Food trucks",     icon: Truck,          hero: { kicker: "food trucks",         h1: "The kitchen comes to you.",          tag: "Follow the food trucks in your city." } },
  { key: "food_cart",                           label: "Food carts",      icon: Truck,          hero: { kicker: "food carts",          h1: "Street eats, close-up.",             tag: "Tacos, hot dogs, burritos and street-food vendors near you." } },
  { key: "farmers_market",                      label: "Farmers markets", icon: Wheat,          hero: { kicker: "farmers markets",     h1: "The market comes to Main Street.",   tag: FP_TAGLINES.farmers_markets } },
  { key: "farm,csa",                            label: "Farms",           icon: Tractor,        hero: { kicker: "farms",               h1: "Fresh from the farm to your table.", tag: "Family farms, CSAs and heritage growers you can visit." } },
  { key: "seafood_shop",                        label: "Seafood",         icon: Store,          hero: { kicker: "seafood",             h1: "From the water to your table.",      tag: "Seafood markets, fishmongers, and fresh catch." } },
  { key: "butcher",                             label: "Butchers",        icon: Store,          hero: { kicker: "butchers",            h1: "The craft of the cut.",              tag: "Traditional butchers in every neighbourhood." } },
  { key: "bar,pub,brewery,winery,distillery,wine_shop,alcohol_shop", label: "Bars & breweries", icon: Store, hero: { kicker: "bars & breweries", h1: "Where the day slows down.",  tag: "Bars, breweries, wineries, and distilleries." } },
  { key: "ice_cream,chocolate_shop,confectionery", label: "Sweets & ice cream", icon: Store,   hero: { kicker: "sweets",              h1: "A little something sweet.",          tag: "Ice cream, chocolate, and confectionery." } },
  { key: "food_bank,food_pantry,food_charity,soup_kitchen", label: "Food banks & charities", icon: HeartHandshake, hero: { kicker: "food banks & charities", h1: "Together against hunger.", tag: FP_TAGLINES.food_charities } },
  { key: "caterer",                             label: "Caterers",        icon: ChefHat,        hero: { kicker: "caterers",            h1: "Elegant dining for every occasion.", tag: "Wedding, corporate and private-event caterers." } },
  { key: "food_distributor,food_warehouse",     label: "Distributors",    icon: Truck,          hero: { kicker: "distributors",        h1: "The engine behind every kitchen.",   tag: "Distributors, warehouses, and cold-chain supply." } },
];

// Metros — split into two visible rows. The first row (`primary`) is
// the 13 biggest US food metros; the second row (`secondary`) is 5
// more with a "More ▾" popover for the long tail.
const METROS_PRIMARY = [
  { slug: "",                     label: "All metros"       },
  { slug: "new-york",             label: "New York"         },
  { slug: "los-angeles",          label: "Los Angeles"      },
  { slug: "chicago",              label: "Chicago"          },
  { slug: "dallas-fort-worth",    label: "Dallas–Fort Worth"},
  { slug: "houston",              label: "Houston"          },
  { slug: "atlanta",              label: "Atlanta"          },
  { slug: "washington-dc",        label: "Washington DC"    },
  { slug: "philadelphia",         label: "Philadelphia"     },
  { slug: "miami",                label: "Miami"            },
  { slug: "phoenix",              label: "Phoenix"          },
  { slug: "portland",             label: "Portland"         },
  { slug: "seattle",              label: "Seattle"          },
  { slug: "san-francisco",        label: "San Francisco"    },
];
const METROS_SECONDARY = [
  { slug: "minneapolis",          label: "Minneapolis"      },
  { slug: "denver",               label: "Denver"           },
  { slug: "salt-lake-city",       label: "Salt Lake City"   },
  { slug: "las-vegas",            label: "Las Vegas"        },
  { slug: "san-jose",             label: "San Jose"         },
];
const METROS_MORE = [
  { slug: "boston",               label: "Boston"           },
  { slug: "detroit",              label: "Detroit"          },
  { slug: "san-diego",            label: "San Diego"        },
  { slug: "tampa",                label: "Tampa"            },
  { slug: "orlando",              label: "Orlando"          },
  { slug: "st-louis",             label: "St. Louis"        },
  { slug: "pittsburgh",           label: "Pittsburgh"       },
  { slug: "cincinnati",           label: "Cincinnati"       },
  { slug: "kansas-city",          label: "Kansas City"      },
  { slug: "indianapolis",         label: "Indianapolis"     },
  { slug: "nashville",            label: "Nashville"        },
  { slug: "austin",               label: "Austin"           },
  { slug: "san-antonio",          label: "San Antonio"      },
  { slug: "sacramento",           label: "Sacramento"       },
  { slug: "raleigh-durham",       label: "Raleigh-Durham"   },
  { slug: "charlotte",            label: "Charlotte"        },
  { slug: "columbus",             label: "Columbus"         },
  { slug: "milwaukee",            label: "Milwaukee"        },
];
const METROS = [...METROS_PRIMARY, ...METROS_SECONDARY, ...METROS_MORE];

function prettyType(t) {
  if (!t) return "Food business";
  return t.replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
}

export default function FoodBusinessesDirectory() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fallback, setFallback] = useState([]);

  const q = params.get("q") || "";
  const type = params.get("type") || "";
  const metro = params.get("metro") || "";
  const state = params.get("state") || "";

  useEffect(() => {
    document.title = "Food Businesses — US Directory · Food Pulse";
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/food-businesses/stats`);
        if (!cancelled) setStats(res.data);
      } catch { /* stats optional */ }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFallback([]);
    (async () => {
      try {
        const res = await axios.get(`${API}/api/food-businesses`, {
          params: {
            q: q || undefined,
            business_type: type || undefined,
            metro: metro || undefined,
            state: state || undefined,
            limit: 60,
          },
        });
        if (cancelled) return;
        setItems(res.data.items || []);
        setTotal(res.data.total || 0);
        // If the strict filter returned 0 results but the user picked
        // a specific type or metro, fetch a broader fallback set (same
        // type, no metro restriction) so the page never renders empty.
        if ((res.data.items || []).length === 0 && (type || metro)) {
          try {
            const fb = await axios.get(`${API}/api/food-businesses`, {
              params: {
                business_type: type || undefined,   // keep the type — that's what the user asked for
                q: q || undefined,
                limit: 12,                          // small nearest set
              },
            });
            if (!cancelled) setFallback(fb.data.items || []);
          } catch { /* fallback optional */ }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [q, type, metro, state]);

  const setParam = (key, val) => {
    const next = new URLSearchParams(params);
    if (val) next.set(key, val); else next.delete(key);
    setParams(next, { replace: true });
  };

  // Toggle for the "More ▾" metro popover (long-tail metros).
  const [metroMoreOpen, setMetroMoreOpen] = useState(false);
  // If the currently selected metro is in the More list, auto-expand
  // so the active chip stays visible.
  const activeInMore = METROS_MORE.some((m) => m.slug === metro);
  useEffect(() => { if (activeInMore) setMetroMoreOpen(true); }, [activeInMore]);

  // Resolve the active hero from the current type filter — every chip
  // carries its own kicker/h1/tagline so the directory page reads as
  // a purpose-built landing page for every food-business vertical.
  const activeChip = TYPE_CHIPS.find((c) => c.key === (type || "")) || TYPE_CHIPS[0];
  const hero = activeChip.hero;
  // Pick a branded illustration for the current filter (first type in
  // the comma-separated key). Falls back to null → hero renders text-only.
  const heroIllustration = categoryHeroIllustration((type || "").split(",")[0]);

  return (
    <div className="min-h-screen" style={{ background: "#FBF7F0" }} data-testid="food-businesses-directory">
      <SeoHead
        section="food-pulse"
        title={`${hero.h1} — Food Pulse`}
        description={hero.tag}
      />

      {/* Hero */}
      <section
        className="border-b"
        style={{ borderColor: "#E9D8B4", background: "linear-gradient(180deg,#F0E2C7 0%,#FBF7F0 100%)" }}
      >
        <div className="max-w-6xl mx-auto px-4 py-12 grid gap-8 md:grid-cols-[minmax(0,1fr)_auto] items-center">
          <div>
          <div
            className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest rounded-full px-3 py-1 mb-4"
            style={{ background: "#fff", border: "1px solid #C79A3F", color: "#8B5E10" }}
          >
            <Store className="w-3 h-3" /> Food Pulse · {hero.kicker}
          </div>
          <FoodPulseSlogan surface="businesses" size="lg" className="mt-3 mb-1" testId="fp-business-slogan" />
          <h1
            className="font-display font-bold leading-tight text-4xl sm:text-5xl lg:text-6xl mb-3"
            style={{ color: "#3E2A0A" }}
            data-testid="business-hero-h1"
          >
            {hero.h1}
          </h1>
          <p className="text-base sm:text-lg max-w-3xl" style={{ color: "#6B4A16" }} data-testid="business-hero-tag">
            {hero.tag}
          </p>

          <div className="mt-6 flex flex-wrap gap-3 items-center max-w-2xl">
            <div
              className="flex-1 min-w-[240px] flex items-center gap-2 rounded-full border bg-white px-4 py-2 shadow-sm"
              style={{ borderColor: "#C79A3F" }}
            >
              <Search className="w-4 h-4 opacity-60" />
              <input
                type="search"
                data-testid="business-search-input"
                placeholder="Search — pizzeria, farmers market, food bank, seafood…"
                value={q}
                onChange={(e) => setParam("q", e.target.value)}
                className="w-full bg-transparent outline-none text-sm"
              />
            </div>
            <div className="text-xs opacity-70" data-testid="business-total-count">
              {loading ? "Loading…" : `${total.toLocaleString()} business${total === 1 ? "" : "es"}`}
            </div>
            {/* Mobile app CTA — visible only on desktop widths since mobile
                users are already on the mobile shell. Deep-links to /m. */}
            <Link
              to="/m"
              data-testid="business-mobile-app-cta"
              className="hidden md:inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border shadow-sm hover:shadow transition-all"
              style={{ background: "#B25D38", borderColor: "#B25D38", color: "#fff" }}
            >
              <Smartphone className="w-3.5 h-3.5" />
              Try the mobile app →
            </Link>
          </div>

          {stats && stats.total > 0 && (
            <div className="mt-6 flex flex-wrap gap-4 text-xs" style={{ color: "#6B4A16" }} data-testid="business-stats">
              <div><b>{stats.total.toLocaleString()}</b> total locations</div>
              <div><b>{stats.total_brands.toLocaleString()}</b> brands / chains</div>
              {stats.by_type?.slice(0, 3).map((t) => (
                <div key={t.type}><b>{t.count.toLocaleString()}</b> {prettyType(t.type)}</div>
              ))}
            </div>
          )}
          </div>

          {heroIllustration && (
            <div className="hidden md:block" data-testid="business-hero-illustration">
              <img
                src={heroIllustration}
                alt={`${hero.kicker} illustration`}
                loading="lazy"
                className="w-[280px] lg:w-[360px] h-auto rounded-xl shadow-md"
                style={{ border: "1px solid #C79A3F" }}
              />
            </div>
          )}
        </div>
      </section>

      {/* Type chips */}
      <div className="max-w-6xl mx-auto px-4 pt-6">
        <div className="flex flex-wrap gap-2" data-testid="business-type-chips">
          {TYPE_CHIPS.map((c) => {
            const active = (type || "") === c.key;
            const Icon = c.icon;
            return (
              <button
                key={c.key || "all"}
                type="button"
                onClick={() => setParam("type", c.key)}
                data-testid={`biz-type-${c.key.split(",")[0] || "all"}`}
                className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition-colors ${
                  active ? "text-white" : "hover:bg-white"
                }`}
                style={active
                  ? { background: "#8B5E10", borderColor: "#8B5E10" }
                  : { background: "#fff", borderColor: "#C79A3F", color: "#3E2A0A" }}
              >
                <Icon className="w-3.5 h-3.5" />
                {c.label}
              </button>
            );
          })}
        </div>

        {/* Metro filter — two visible rows + a "More ▾" popover for
            the long tail. Primary row = 13 biggest US food metros;
            secondary row = 5 more; More popover holds the rest. */}
        <div className="flex flex-wrap gap-2 mt-3" data-testid="business-metro-chips">
          {METROS_PRIMARY.map((m) => {
            const active = (metro || "") === m.slug;
            return (
              <button
                key={m.slug || "all"}
                type="button"
                onClick={() => setParam("metro", m.slug)}
                data-testid={`biz-metro-${m.slug || "all"}`}
                className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
                  active ? "text-white" : "hover:bg-white"
                }`}
                style={active
                  ? { background: "#3E2A0A", borderColor: "#3E2A0A" }
                  : { background: "#fff", borderColor: "#E9D8B4", color: "#6B4A16" }}
              >
                {m.label}
              </button>
            );
          })}
        </div>
        <div className="flex flex-wrap gap-2 mt-2 items-center" data-testid="business-metro-chips-2">
          {METROS_SECONDARY.map((m) => {
            const active = (metro || "") === m.slug;
            return (
              <button
                key={m.slug}
                type="button"
                onClick={() => setParam("metro", m.slug)}
                data-testid={`biz-metro-${m.slug}`}
                className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
                  active ? "text-white" : "hover:bg-white"
                }`}
                style={active
                  ? { background: "#3E2A0A", borderColor: "#3E2A0A" }
                  : { background: "#fff", borderColor: "#E9D8B4", color: "#6B4A16" }}
              >
                {m.label}
              </button>
            );
          })}
          <button
            type="button"
            onClick={() => setMetroMoreOpen((s) => !s)}
            data-testid="biz-metro-more-toggle"
            aria-expanded={metroMoreOpen}
            className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full border hover:bg-white transition-colors"
            style={{ background: "#fff", borderColor: "#C79A3F", color: "#8B5E10" }}
          >
            {metroMoreOpen ? "Less" : "More"}
            <ChevronDown className={`w-3 h-3 transition-transform ${metroMoreOpen ? "rotate-180" : ""}`} />
          </button>
        </div>
        {metroMoreOpen && (
          <div className="flex flex-wrap gap-2 mt-2" data-testid="business-metro-chips-more">
            {METROS_MORE.map((m) => {
              const active = (metro || "") === m.slug;
              return (
                <button
                  key={m.slug}
                  type="button"
                  onClick={() => setParam("metro", m.slug)}
                  data-testid={`biz-metro-${m.slug}`}
                  className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
                    active ? "text-white" : "hover:bg-white"
                  }`}
                  style={active
                    ? { background: "#3E2A0A", borderColor: "#3E2A0A" }
                    : { background: "#fff", borderColor: "#E9D8B4", color: "#6B4A16" }}
                >
                  {m.label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Grid */}
      <section className="max-w-6xl mx-auto px-4 py-8">
        {loading ? (
          <div className="text-sm opacity-70" data-testid="business-loading">Loading businesses…</div>
        ) : items.length === 0 ? (
          <>
            <div
              className="rounded-2xl border p-10 text-center"
              style={{ borderColor: "#C79A3F", background: "#fff" }}
              data-testid="business-empty"
            >
              <Sparkles className="w-6 h-6 mx-auto mb-2" style={{ color: "#8B5E10" }} />
              <div className="font-display text-lg mb-1">
                {metro
                  ? `No ${activeChip.label.toLowerCase()} in ${(METROS.find((m) => m.slug === metro) || {}).label || "this metro"} yet.`
                  : "No businesses match this filter yet."}
              </div>
              <div className="text-sm opacity-70">
                {fallback.length > 0
                  ? "Showing nearest listings from other metros — pick a different city above or clear filters."
                  : "We're ingesting the top 20 US metros right now. Check back shortly — or clear filters to see the full set."}
              </div>
              <div className="mt-4 flex flex-wrap gap-2 justify-center items-center">
                {metro && (
                  <button
                    type="button"
                    onClick={() => setParam("metro", "")}
                    data-testid="business-empty-clear-metro"
                    className="text-xs font-semibold px-3 py-1.5 rounded-full border hover:bg-black/[0.02]"
                    style={{ borderColor: "#C79A3F", color: "#8B5E10", background: "#fff" }}
                  >
                    See {activeChip.label.toLowerCase()} everywhere →
                  </button>
                )}
                {type && (
                  <button
                    type="button"
                    onClick={() => { setParam("type", ""); setParam("metro", metro); }}
                    data-testid="business-empty-clear-type"
                    className="text-xs font-semibold px-3 py-1.5 rounded-full border hover:bg-black/[0.02]"
                    style={{ borderColor: "#C79A3F", color: "#8B5E10", background: "#fff" }}
                  >
                    See every business in this metro →
                  </button>
                )}
              </div>
            </div>
            {fallback.length > 0 && (
              <div className="mt-8" data-testid="business-fallback">
                <div className="text-[10px] font-mono uppercase tracking-widest mb-3" style={{ color: "#B25D38" }}>
                  — Nearest {activeChip.label.toLowerCase()} across the US
                </div>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {fallback.map((b) => {
                    const cover = (b.images || [])[0] || categoryIllustration(b.business_type, b.slug, {
                      subType: b.sub_type, cuisines: b.cuisines, name: b.name,
                    });
                    return (
                      <Link
                        key={b.slug}
                        to={`/food-pulse/businesses/${b.slug}`}
                        className="group rounded-2xl bg-white border overflow-hidden hover:shadow-md transition-shadow"
                        style={{ borderColor: "#E9D8B4" }}
                        data-testid={`business-fallback-card-${b.slug}`}
                      >
                        <div className="aspect-[4/3] bg-black/5 overflow-hidden">
                          <img src={cover} alt={b.name} className="w-full h-full object-cover" loading="lazy" />
                        </div>
                        <div className="p-4">
                          <div className="text-[10px] font-mono uppercase tracking-widest opacity-60">{prettyType(b.business_type)}</div>
                          <div className="font-display text-lg mt-0.5 leading-tight">{b.name}</div>
                          {(b.city || b.state_code) && (
                            <div className="text-xs opacity-70 mt-0.5 inline-flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {[b.city, b.state_code].filter(Boolean).join(", ")}
                            </div>
                          )}
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="business-grid">
            {items.map((b) => {
              const cover = (b.images || [])[0] || categoryIllustration(b.business_type, b.slug, {
                subType: b.sub_type,
                cuisines: b.cuisines,
                name: b.name,
              });
              return (
                <Link
                  key={b.slug}
                  to={`/food-pulse/businesses/${b.slug}`}
                  className="group rounded-2xl bg-white border overflow-hidden hover:shadow-md transition-shadow"
                  style={{ borderColor: "#E9D8B4" }}
                  data-testid={`business-card-${b.slug}`}
                >
                  <div className="aspect-[4/3] bg-black/5 overflow-hidden">
                    <img
                      src={cover}
                      alt={b.name}
                      className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-4">
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-[10px] font-mono uppercase tracking-widest opacity-60">
                        {prettyType(b.business_type)}
                      </div>
                      {b.verified && (
                        <span
                          className="inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded-full"
                          style={{ background: "#DCEAD1", color: "#2F6B2F" }}
                        >
                          <BadgeCheck className="w-3 h-3" /> Verified
                        </span>
                      )}
                    </div>
                    <div className="font-display text-lg mt-0.5 leading-tight" data-testid={`business-name-${b.slug}`}>
                      {b.name}
                    </div>
                    {(b.city || b.state_code) && (
                      <div className="text-xs opacity-70 mt-0.5 inline-flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {[b.city, b.state_code].filter(Boolean).join(", ")}
                      </div>
                    )}
                    {b.cuisines && b.cuisines.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {b.cuisines.slice(0, 3).map((c) => (
                          <span
                            key={c}
                            className="text-[10px] font-mono px-1.5 py-0.5 rounded-full"
                            style={{ background: "#F0E2C7", color: "#8B5E10" }}
                          >
                            {c}
                          </span>
                        ))}
                      </div>
                    )}
                    <div
                      className="mt-3 inline-flex items-center gap-1 text-xs font-semibold group-hover:gap-2 transition-all"
                      style={{ color: "#8B5E10" }}
                    >
                      View profile <ArrowRight className="w-3 h-3" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
